import React, { FC, useCallback, useMemo, useState } from 'react';
import { Exercise, ExerciseSeriesType } from '../../../../../config/api/models/Exercise';
import ExerciseDetail from './ExerciseDetail';
import { GroupStudentSeriesScores } from '../../../../../config/api/models/Score';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { createPortal } from 'react-dom';
import { styled } from '@material-ui/styles';
import { IconButton, Theme, Tooltip, useTheme } from '@material-ui/core';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';

type Props = {
  seriesId: string;
  type: ExerciseSeriesType;
  exercises: Exercise[];
  teacherProgressAndScores?: GroupStudentSeriesScores;
  mode?: 'read' | 'edit';
  onDeleteExercise?: () => void;
  onUpdateExercise?: () => void;
  onOrderExercises?: ({ source, destination }: DropResult) => void;
  onExpandedChanged?: () => void;
};

const StyledToggleAll = styled(IconButton)<Theme>(({ theme }) => ({
  background: theme.palette.grey['200'],
  '&:hover': {
    background: theme.palette.grey['300'],
  },
}));

const ExerciseListHeader = styled('div')<Theme>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 2, 2, 2),
}));

const ExerciseList: FC<Props> = ({
  mode = 'read',
  seriesId,
  type,
  exercises,
  onDeleteExercise,
  onUpdateExercise,
  teacherProgressAndScores,
  onOrderExercises,
  onExpandedChanged,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState<string | false>(
    location.state ? `exercise_${(location.state as any).blockId}` : false
  );

  const handleChange = useCallback(
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : expanded === 'all' ? panel : false);
      if (!!onExpandedChanged) onExpandedChanged();
    },
    [expanded, onExpandedChanged]
  );

  const toggleAll = useCallback(() => setExpanded(expanded === 'all' ? false : 'all'), [expanded]);

  const notes = useAppSelector((state) => state.blockNotes.blockNotes);
  const exercisesWithNotes = useMemo(() => {
    return exercises.map((exercise) => {
      const parent = exercise.parent_id ?? exercise.id;
      const note = notes?.find((note) => note.blockId === parent);

      return { ...exercise, note, pageType: type };
    });
  }, [exercises, notes, type]);

  return (
    <>
      {exercises.length > 0 && (
        <ExerciseListHeader>
          <Tooltip
            title={
              expanded === 'all' ? t('books.exercises.closeAll') : t('books.exercises.openAll')
            }
          >
            <StyledToggleAll onClick={toggleAll} className="hide-on-print">
              <FontAwesomeSpriteIcon
                icon={expanded === 'all' ? SpriteIconsLight.ANGLE_UP : SpriteIconsLight.ANGLE_DOWN}
                color={theme.palette.secondary.main}
              />
            </StyledToggleAll>
          </Tooltip>
        </ExerciseListHeader>
      )}
      {
        {
          edit: !!onOrderExercises && (
            <DragDropContext onDragEnd={onOrderExercises}>
              <Droppable droppableId="EXERCISEROOT">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {exercisesWithNotes.map((exercise: Exercise, index: number) => (
                      <Draggable key={exercise.id} draggableId={exercise.id} index={index}>
                        {({ draggableProps, dragHandleProps, innerRef }, snapshot) => {
                          const result = (
                            <div ref={innerRef} {...draggableProps}>
                              <ExerciseDetail
                                key={exercise.id}
                                index={index}
                                expanded={expanded}
                                onChangeExpanded={handleChange}
                                seriesId={seriesId}
                                pageType={type}
                                exercise={exercise}
                                note={exercise.note}
                                mode={mode}
                                onUpdateExercise={onUpdateExercise}
                                teacherProgressAndScores={teacherProgressAndScores}
                                dragHandleProps={dragHandleProps || undefined}
                              />
                            </div>
                          );

                          if (snapshot.isDragging) {
                            const portal = document.getElementById('dnd_portal_root');
                            if (portal) return createPortal(result, portal);
                          }
                          return result;
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ),
          read: exercisesWithNotes.map((exercise: Exercise, index: number) => (
            <ExerciseDetail
              key={exercise.id}
              index={index}
              expanded={expanded}
              onChangeExpanded={handleChange}
              onSetExpanded={(id) => setExpanded(`exercise_${id as string}`)}
              seriesId={seriesId}
              pageType={type}
              exercise={exercise}
              mode={mode}
              note={exercise.note}
              onUpdateExercise={onUpdateExercise}
              teacherProgressAndScores={teacherProgressAndScores}
            />
          )),
        }[mode]
      }
    </>
  );
};

export default ExerciseList;
